import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useGetAllCampaignsWithVersionsQuery } from "../../RTKQuery/CampaignService/campaignApi";
import DataTable from "../../components/UIKit/CollapsibleTable";
import Loader from "../../components/Loader/Loader";
import PageComponentHeader from "../../components/PageComponentHeader";
import Page from "../../components/Page";
import VersionChip from "../../components/UIKit/Chip/VersionChip";
import CampaignStatusChip from "../../components/UIKit/Chip/CampaignStatusChip";
import { tableContainer } from "./styles";
import { Box, IconButton } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { searchField, searchInput } from "../Dashboard/styles";

export default function VersionHistory() {
  const navigate = useNavigate();
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 25,
  });
  const [sorting, setSorting] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [loader, setLoader] = useState(true);

  const { data, isLoading, isFetching } = useGetAllCampaignsWithVersionsQuery(
    {
      pagination,
      sorting,
      searchQuery,
    },
    {
      refetchOnMountOrArgChange: true,
    }
  );

  useEffect(() => {
    if (data && !isLoading && !isFetching) {
      setLoader(false);
    } else if (isLoading || isFetching) {
      setLoader(true);
    }
  }, [data, isLoading, isFetching, pagination]);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(inputValue);
      setPagination((prev) => ({ ...prev, pageIndex: 0 })); // Reset to first page on search
    }, 500);

    return () => clearTimeout(timer);
  }, [inputValue]);

  const handleSearchChange = (e) => {
    setInputValue(e.target.value);
  };

  const handleSearch = () => {
    setSearchQuery(inputValue);
    setPagination((prev) => ({ ...prev, pageIndex: 0 })); // Reset to first page on search
  };

  const handleRowClick = (row) => {
    navigate(`/versionhistory/details/${row.original.id}`);
  };

  const columns = [
    {
      accessorKey: "campaignTitle",
      header: "Campaign Title",
      enableSorting: false,
      width: 200,
      Cell: ({ cell }) => (
        <div
          onClick={(e) => {
            e.stopPropagation();
            handleRowClick(cell.row);
          }}
          style={{
            cursor: "pointer",
            color: "#367598",
            textDecoration: "underline",
          }}
        >
          {cell?.getValue()}
        </div>
      ),
    },
    {
      accessorKey: "status",
      header: "Campaign Status",
      enableSorting: false,
      width: 40,
      Cell: ({ cell }) => {
        const status = cell?.getValue();
        return <CampaignStatusChip status={status} />;
      },
    },
    {
      accessorKey: "latestVersion",
      header: "Latest Version",
      enableSorting: false,
      width: 150,
      Cell: ({ cell }) => {
        const latestVersion = cell?.getValue();
        if (!latestVersion) return "N/A";

        const type = latestVersion?.versionType;
        const versionNumber = latestVersion?.versionNumber || "";

        // Format: "Exported list V3", "Suppressed list V2.3", "Filtered list V1"
        const typeLabel = type
          ? type.charAt(0).toUpperCase() + type.slice(1)
          : "";
        const label =
          typeLabel && versionNumber
            ? `${typeLabel} list ${versionNumber}`
            : versionNumber || "N/A";

        return <VersionChip label={label} variant="type" type={type} />;
      },
    },
    {
      accessorKey: "versionCount",
      header: "Total List Versions",
      enableSorting: false,
      width: 40,
      //   muiTableHeadCellProps: {
      //     align: "center",
      //   },
      //   muiTableBodyCellProps: {
      //     align: "center",
      //   },
      Cell: ({ cell }) => {
        const count = cell?.getValue();
        return (
          <VersionChip
            label={count !== null && count !== undefined ? count : "N/A"}
            variant="count"
          />
        );
      },
    },
    {
      accessorKey: "createdAt",
      header: "Created On",
      enableSorting: false,
      width: 80,
      Cell: ({ cell }) => {
        const date = cell?.getValue();
        return date
          ? new Date(date).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
          })
          : "N/A";
      },
    },
  ];

  if (isLoading || isFetching) {
    return (
      <div className="noData">
        <Loader />
      </div>
    );
  }

  return (
    <Page>
      <PageComponentHeader allowBack={false} pageTitle={"Version History"}>
        <Box sx={searchField}>
          <input
            type="text"
            value={inputValue}
            style={searchInput}
            className="inputSearch"
            placeholder="Search by Campaign Title..."
            onChange={handleSearchChange}
          />
          <IconButton onClick={handleSearch}>
            <SearchIcon style={{ color: "#367598" }} />
          </IconButton>
        </Box>
      </PageComponentHeader>

      {!data || (data?.campaigns && data.campaigns.length === 0) ? (
        <h3 className="noData">No Campaigns with Versions Found</h3>
      ) : (
        <div style={tableContainer}>
          <DataTable
            columns={columns}
            data={data?.campaigns || []}
            totalData={data?.total || 0}
            sorting={sorting}
            setSorting={setSorting}
            pagination={pagination}
            setPagination={setPagination}
            setLoader={setLoader}
            enableCollapsibleRow={false}
            manualPagination={true}
          />
        </div>
      )}
    </Page>
  );
}
